// packages/kb-view/src/remark-wikilinks.ts
//
// LAT-1157 (R3) — a small remark (mdast) transform that rewrites Obsidian-style
// `[[target]]` / `[[target|label]]` / `![[target]]` spans in TEXT nodes into
// link nodes, so a MarkdownView `a`-override can render them as navigable /
// muted references. The plugin is opt-in per caller: a surface that does not
// pass it never runs it, so `[[x]]` stays literal text.
//
// LAT-1605 — moved here from apps/web unchanged. The transform is pure mdast
// and has no React, DOM, or application dependency, so the docs worker runs the
// exact same wikilink grammar the in-app reader does.
//
// Bodies are never rewritten (the plugin transforms the render tree only, never
// the stored file). Code is untouched by construction: fenced/indented code
// becomes `code` nodes and inline code becomes `inlineCode` nodes — NEITHER
// carries child `text` nodes, so the text-only walk below can never descend
// into them.
//
// No new npm dependency and no reliance on `unist-util-visit` (only
// transitively present): the tree is walked by hand against minimal local mdast
// shapes. The exact target is carried out-of-band on `data-wikilink-target`
// (hProperties are copied verbatim, unlike `url`, which mdast-util-to-hast
// percent-normalizes and would corrupt a raw-space target).

import { completeWikilinkExtension } from "./kb-link-target";

interface MdNode {
  type: string;
  children?: MdNode[];
  value?: string;
}

interface TextNode {
  type: "text";
  value: string;
}

interface LinkNode {
  type: "link";
  url: string;
  title: null;
  children: TextNode[];
  data: { hProperties: Record<string, string> };
}

// The `.md` completion for the fallback `url` is the ONE rule in
// `./kb-link-target` (LAT-1867) — the same one the server extractor and the
// application's kb-links resolver read. Resolution itself runs off the raw
// `data-wikilink-target`, not off this url.

/**
 * Try to parse a wikilink whose inner span starts at `innerStart` (just after
 * the opening `[[`). Returns the link node + the index just past the closing
 * `]]`, or null when there is no `]]` before the next newline (wikilinks never
 * span lines — the `[[` is then left as literal text).
 */
function parseWikilink(value: string, innerStart: number): { node: LinkNode; end: number } | null {
  for (let k = innerStart; k < value.length - 1; k++) {
    const c = value[k];
    if (c === "\n") return null;
    if (c === "]" && value[k + 1] === "]") {
      const inner = value.slice(innerStart, k);
      const pipe = inner.indexOf("|");
      const target = (pipe === -1 ? inner : inner.slice(0, pipe)).trim();
      const label = pipe === -1 ? "" : inner.slice(pipe + 1).trim();
      if (target === "") return null;
      const display = label !== "" ? label : target;
      const withoutFragment = target.split("#", 1)[0]!.trim();
      const node: LinkNode = {
        type: "link",
        url: completeWikilinkExtension(withoutFragment),
        title: null,
        children: [{ type: "text", value: display }],
        // Exact target for the resolver; presence flag for the a-override.
        data: { hProperties: { "data-wikilink": "true", "data-wikilink-target": target } },
      };
      return { node, end: k + 2 };
    }
  }
  return null;
}

/**
 * Split one text node's value into a run of text/link nodes. `[[…]]` and the
 * `![[…]]` embed form both mint a link edge (embeds are NOT transcluded — F1b).
 * Returns the original single text node unchanged when it holds no wikilink.
 */
function splitText(value: string): MdNode[] {
  const out: MdNode[] = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf !== "") {
      out.push({ type: "text", value: buf } as MdNode);
      buf = "";
    }
  };
  while (i < value.length) {
    const isEmbed = value[i] === "!" && value[i + 1] === "[" && value[i + 2] === "[";
    const isLink = value[i] === "[" && value[i + 1] === "[";
    if (isEmbed || isLink) {
      const innerStart = i + (isEmbed ? 3 : 2);
      const parsed = parseWikilink(value, innerStart);
      if (parsed) {
        flush();
        out.push(parsed.node as unknown as MdNode);
        i = parsed.end;
        continue;
      }
    }
    buf += value[i];
    i++;
  }
  flush();
  return out.length > 0 ? out : [{ type: "text", value } as MdNode];
}

function walk(node: MdNode): void {
  if (!Array.isArray(node.children)) return;
  const next: MdNode[] = [];
  for (const child of node.children) {
    if (child.type === "text" && typeof child.value === "string" && child.value.includes("[[")) {
      next.push(...splitText(child.value));
    } else {
      walk(child);
      next.push(child);
    }
  }
  node.children = next;
}

/** remark plugin: `remarkPlugins={[remarkWikilinks]}`. */
export function remarkWikilinks() {
  return (tree: MdNode): void => {
    walk(tree);
  };
}
